import React, { forwardRef, useState } from 'react';
import { PropTypes } from '../../../utils/propTypes';
import styles from './Switch.module.css';

/**
 * Switch — an on/off toggle track + thumb, reimplemented in Athena's idiom
 * (JSX + CSS Module + 2.0 tokens). Flat + fully mode-invariant (track
 * var(--input) → var(--primary), thumb var(--background) — all cascade), so no
 * [data-theme] overrides. Controlled (`checked` + `onCheckedChange`) /
 * uncontrolled (`defaultChecked`); `size` 'sm' | 'default'. Preview-only.
 */
const Switch = forwardRef(
  (
    {
      checked,
      defaultChecked = false,
      onCheckedChange,
      disabled = false,
      size = 'default',
      className = '',
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) => {
    const isControlled = checked !== undefined;
    const [internal, setInternal] = useState(defaultChecked);
    const on = isControlled ? checked : internal;

    const toggle = (e) => {
      if (disabled) return;
      props.onClick?.(e);
      const next = !on;
      if (!isControlled) setInternal(next);
      onCheckedChange?.(next);
    };

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={on}
        aria-invalid={ariaInvalid}
        data-state={on ? 'checked' : 'unchecked'}
        disabled={disabled}
        className={`${styles.track} ${size === 'sm' ? styles.sm : ''} ${className}`}
        {...props}
        onClick={toggle}
      >
        <span className={styles.thumb} aria-hidden="true" />
      </button>
    );
  }
);

Switch.displayName = 'Switch';

Switch.propTypes = {
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  onCheckedChange: PropTypes.func,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  className: PropTypes.string,
  'aria-invalid': PropTypes.bool,
  onClick: PropTypes.func,
};

export default Switch;
export { Switch };
